import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, X, Loader2, FileText, MapPin, Briefcase, ExternalLink, Send } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { CandidateAvatar } from "./CandidateAvatar";
import { CandidateProfileContent } from "./CandidateProfileSections";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline employer-facing candidate view — renders as a sheet ON TOP of
// CompanyDashboard (no route change, no unmount). Reuses the exact same
// public profile data/sections (CandidateProfileContent) as the candidate's
// public page, but with a compact header instead of the public hero, so the
// employer never loses the 5-item dashboard shell/BottomNav.
export default function EmployerCandidateView({ app, onClose, onViewCv, t, autoOpenComposer = false }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [composing, setComposing] = useState(autoOpenComposer);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        // Authenticated (not anonymous) so a real "Quién me ve" visit gets
        // logged for the candidate, tied to the job this application is for.
        const res = await axios.get(`${API}/profile/${app.user_id}`, {
          headers: getAuthHeaders(), params: { related_job_id: app.job_id },
        });
        if (active) setProfile(res.data);
        if (active) track("candidate_viewed", { candidate_user_id: app.user_id, job_id: app.job_id });
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [app.user_id, app.job_id]);

  const sendMessage = async () => {
    if (!messageText.trim()) return;
    setSending(true);
    setSendError("");
    try {
      const res = await axios.post(`${API}/conversations`,
        { candidate_id: app.user_id, job_id: app.job_id, message: messageText.trim() },
        { headers: getAuthHeaders() });
      navigate(`/messages?c=${res.data.conversation_id}`);
    } catch (err) {
      setSendError(err.response?.data?.detail || t("messages.sendError"));
    } finally {
      setSending(false);
    }
  };

  const hasCv = !!(app.cv_type || app.cv_filename);

  return (
    <div className="fixed inset-0 bg-black/50 z-[55] flex items-end sm:items-center justify-center" data-testid="employer-candidate-view" onClick={onClose}>
      <div
        className="bg-white w-full h-[92dvh] rounded-t-2xl sm:h-[85vh] sm:max-w-3xl sm:rounded-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Compact header — NOT the public profile's big hero */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <button onClick={onClose} data-testid="employer-candidate-back" className="p-2 -ml-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <CandidateAvatar name={app.name} picture={profile?.picture} size={40} rounded="lg" />
          <div className="min-w-0 flex-1">
            <h3 className="font-poppins font-bold text-base text-slate-900 truncate">{app.name}</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {profile?.title && <p className="text-xs text-slate-500 truncate">{profile.title}</p>}
              {profile?.location && (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <MapPin className="h-3 w-3" /> {profile.location}
                </span>
              )}
            </div>
          </div>
          {hasCv && (
            <button
              onClick={() => onViewCv(app)}
              data-testid="employer-candidate-view-cv"
              className="hidden sm:flex items-center gap-2 h-9 px-4 rounded-xl border border-[#028CF8]/30 text-[#028CF8] text-sm font-semibold hover:bg-[#028CF8]/5 transition-colors flex-shrink-0"
            >
              <FileText className="h-4 w-4" /> {t("company.viewCv")}
            </button>
          )}
          <button
            onClick={() => setComposing((v) => !v)}
            data-testid="employer-candidate-message-button"
            className="hidden sm:flex items-center gap-2 h-9 px-4 rounded-xl bg-[#028CF8] text-white text-sm font-semibold hover:bg-[#0270C5] transition-colors flex-shrink-0"
          >
            {t("messages.sendMessage")}
          </button>
          <button onClick={onClose} data-testid="employer-candidate-close" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Application context strip */}
        <div className="flex items-center gap-4 px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex-shrink-0 overflow-x-auto">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-[#028CF8] whitespace-nowrap">
            <Briefcase className="h-3 w-3" /> {app.job_title}
          </span>
          {app.linkedin && (
            <a href={app.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-slate-400 whitespace-nowrap">
              <ExternalLink className="h-3 w-3" /> LinkedIn
            </a>
          )}
        </div>

        {/* Inline message composer */}
        {composing && (
          <div className="px-5 py-3 border-b border-slate-100 flex-shrink-0 bg-white" data-testid="employer-candidate-composer">
            <textarea
              data-testid="employer-candidate-message-input"
              rows={3}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={t("messages.messagePlaceholder")}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none"
            />
            {sendError && <p className="text-xs text-red-600 mt-1" data-testid="employer-candidate-message-error">{sendError}</p>}
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setComposing(false)} data-testid="employer-candidate-message-cancel" className="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold">
                {t("profTab.cancel")}
              </button>
              <button onClick={sendMessage} disabled={sending || !messageText.trim()} data-testid="employer-candidate-message-send"
                className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#028CF8] text-white text-sm font-semibold disabled:opacity-50">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("messages.send")}
              </button>
            </div>
          </div>
        )}

        {/* Body — scrollable, reuses the real candidate data sections */}
        <div className="flex-1 min-h-0 overflow-y-auto bg-[#F8FAFC] p-4 sm:p-6" data-testid="employer-candidate-body">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-7 w-7 text-[#028CF8] animate-spin" />
            </div>
          )}
          {!loading && error && (
            <p className="text-sm text-slate-500 text-center py-16" data-testid="employer-candidate-error">
              {t("cvViewer.loadError")}
            </p>
          )}
          {!loading && !error && profile && <CandidateProfileContent profile={profile} t={t} />}
        </div>

        {/* Mobile CV + message shortcuts (header buttons hide on small screens for space) */}
        {(hasCv || true) && (
          <div className="sm:hidden flex-shrink-0 px-5 py-3 border-t border-slate-100 flex gap-2" style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
            {hasCv && (
              <button
                onClick={() => onViewCv(app)}
                data-testid="employer-candidate-view-cv-mobile"
                className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-[#028CF8]/30 text-[#028CF8] text-sm font-semibold hover:bg-[#028CF8]/5 transition-colors"
              >
                <FileText className="h-4 w-4" /> {t("company.viewCv")}
              </button>
            )}
            <button
              onClick={() => setComposing((v) => !v)}
              data-testid="employer-candidate-message-button-mobile"
              className="flex-1 flex items-center justify-center h-11 rounded-xl bg-[#028CF8] text-white text-sm font-semibold hover:bg-[#0270C5] transition-colors"
            >
              {t("messages.sendMessage")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
